import { useCallback, useMemo } from 'react';

import { type TBlock } from '@wp-block-builder/package';

import { createFastContext } from '@waypoint/react/fast-store';

import { getBlockForLayout, useCurrentLayout } from '../../stores/BlockLayoutStore';
import { type TBlockButton } from '../../views/EditableMessage/components/AddBlockButton/blocks/helpers/defineBlockButton';

type TAddButtonCallback = (button: TBlockButton) => void;

type TState = {
  currentAnchorElement: HTMLElement | null;
  addButtonCallback: TAddButtonCallback | null;
};

const { Provider, useStore } = createFastContext<TState>({
  currentAnchorElement: null,
  addButtonCallback: null,
});

export const AddBlockMenuStoreProvider = Provider;

function generateStateHook<TValue>(name: keyof TState) {
  return () => {
    const [value, setStore] = useStore((store) => store[name] as TValue);
    const setter = useCallback(
      (value: TValue) => {
        setStore({ [name]: value });
      },
      [setStore]
    );
    return useMemo(() => [value, setter] as const, [value, setter]);
  };
}

export const useCurrentAddMenuAnchorElement = generateStateHook<TState['currentAnchorElement']>('currentAnchorElement');

export function useOpenAddBlockMenu() {
  const currentLayout = useCurrentLayout();
  const [, setStore] = useStore((store) => store.currentAnchorElement);
  return useCallback(
    (currentAnchorElement: HTMLElement, addButtonCallback: (block: TBlock) => void) => {
      setStore({
        currentAnchorElement,
        addButtonCallback: (button) => {
          const block = getBlockForLayout(button, currentLayout);
          addButtonCallback(block);
          setStore({ currentAnchorElement: null, addButtonCallback: null });
        },
      });
    },
    [setStore, currentLayout]
  );
}

export function useInvokeAddBlock() {
  const [addButtonCallback] = useStore((store) => store.addButtonCallback);
  return useCallback(
    (button: TBlockButton) => {
      if (addButtonCallback) {
        addButtonCallback(button);
      }
    },
    [addButtonCallback]
  );
}
